import React, { useState, useEffect } from "react";
import { Input, Button, Table, message, Select } from "antd";
import ModalWrapper from "../../Modal";
import { Trash2 } from "lucide-react";
import { postRequest, getRequest } from "../../service/index";

const AddDepartmentModal = ({ open, onRequestClose }) => {
  const [subDepartments, setSubDepartments] = useState([]);
  const [nextId, setNextId] = useState(1);
  const [departmentName, setDepartmentName] = useState("");
  const [headOfDepartment, setHeadOfDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchEmployees();
    }
  }, [open]);

  const fetchEmployees = async () => {
    try {
      setEmployeeLoading(true);
      const response = await getRequest('/api/companies/reportto');
      if (response && response.employees) {
        setEmployees(response.employees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      message.error('Failed to fetch employees');
    } finally {
      setEmployeeLoading(false);
    }
  };

  const columns = [
    {
      title: "Sr.No",
      dataIndex: "id",
      key: "id",
      width: "20%"
    },
    {
      title: "Sub Department Name", 
      dataIndex: "name",
      key: "name",
      width: "60%",
      render: (_, record) => (
        <Input
          placeholder="Enter sub department name"
          value={record.name}
          onChange={(e) => {
            const newSubDepts = subDepartments.map(dept =>
              dept.id === record.id ? {...dept, name: e.target.value} : dept
            );
            setSubDepartments(newSubDepts);
          }}
        />
      )
    },
    {
      title: "Actions",
      key: "actions",
      width: "20%",
      render: (_, record) => (
        <Button
          type="text"
          icon={<Trash2 className="w-4 h-4 text-red-600" />}
          onClick={() => {
            const newSubDepts = subDepartments.filter(dept => dept.id !== record.id);
            setSubDepartments(newSubDepts);
          }}
        />
      )
    }
  ];

  const handleAddSubDepartment = () => {
    setSubDepartments([...subDepartments, { id: nextId, name: "" }]);
    setNextId(nextId + 1);
  };

  const handleSubmit = async () => {
    if (!departmentName ) {
      message.error("Please fill in all required fields");
      return;
    }

    const payload = {
      name: departmentName,
      headOfDepartment: headOfDepartment,
      subDepartments: subDepartments.map(dept => ({
        name: dept.name
      }))
    };

    try {
      setLoading(true);
      const response = await postRequest("/api/department", payload);
      if (response) {
        message.success("Department added successfully");
        onRequestClose();
        setDepartmentName("");
        setHeadOfDepartment(null);
        setSubDepartments([]);
        setNextId(1);
      }
    } catch (error) {
      message.error("Failed to add department");
      console.error("Error adding department:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Department" open={open} onRequestClose={onRequestClose}>
      <div className="flex flex-col gap-4  px-2">
        <div>
          <label className="block mb-1">Name*</label>
          <Input 
            placeholder="e.g Accounts Department" 
            value={departmentName}
            onChange={(e) => setDepartmentName(e.target.value)}
          />
        </div>

        <div>
          <label className="block mb-1">Head Of Department*</label>
          <Select
            showSearch
            placeholder="Search Employee"
            value={headOfDepartment}
            onChange={setHeadOfDepartment}
            loading={employeeLoading}
            options={employees.map(emp => ({
              value: emp._id,
              label: `${emp.name} | ${emp.department} | ${emp.designation}`
            }))}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            className="w-full"
          />
        </div>

        <div>
          <div className="flex justify-end mb-2">
            <Button
              type="primary"
              onClick={handleAddSubDepartment}
              className="bg-primary hover:bg-primary/80 flex items-center"
            >
              + Add Sub Department
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={subDepartments}
            scroll={{ x: 'max-content' }}
            pagination={false}
            rowKey="id"
          />
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={onRequestClose}>Cancel</Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            loading={loading}
            className="bg-primary hover:bg-primary/80"
          >
            Save
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default AddDepartmentModal;